import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Ruler, Plus, Search, Trash2, X } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Skeleton } from '../../components/ui/Skeleton'
import toast from 'react-hot-toast'

export default function UnitsPage() {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [form, setForm] = useState({
    name: '',
    symbol: ''
  })

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['/products/units', { search }],
    queryFn: () => api.get('/products/units', { params: { search } }).then(r => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (newUn: typeof form) => api.post('/products/units', newUn),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/products/units'] })
      toast.success('واحد سنجش جدید اضافه شد')
      setIsOpen(false)
      setForm({ name: '', symbol: '' })
    },
    onError: () => toast.error('خطا در ایجاد واحد سنجش')
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/products/units/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/products/units'] })
      toast.success('واحد سنجش حذف شد')
    },
    onError: () => toast.error('خطا در حذف واحد سنجش')
  })

  const items = Array.isArray(data) ? data : (data?.items || [])

  return (
    <div className="p-4 space-y-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-neutral-900 dark:text-white">واحدهای سنجش</h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">تعریف واحدهای اندازه‌گیری کالاها (عدد، کیلوگرم، متر...)</p>
        </div>
        <Button onClick={() => setIsOpen(true)}>
          <Plus className="w-4 h-4 ml-1" />
          واحد جدید
        </Button>
      </motion.div>

      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input placeholder="جستجوی واحد..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="card p-4"><Skeleton className="h-5 w-48" /></div>)}</div>
      ) : items.length === 0 ? (
        <div className="card p-12 text-center">
          <Ruler className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
          <p className="text-neutral-500 dark:text-neutral-400">واحدی تعریف نشده است</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 dark:bg-neutral-800">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">عنوان واحد</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">نماد / علامت اختصاری</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {items.map((item: any) => (
                  <tr key={item.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
                    <td className="px-4 py-3 text-sm font-semibold text-neutral-900 dark:text-white">{item.name}</td>
                    <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-300 font-mono">{item.symbol || '---'}</td>
                    <td className="px-4 py-3 text-sm text-left">
                      <button
                        onClick={() => deleteMutation.mutate(item.id)}
                        className="text-red-500 hover:text-red-700 p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20"
                        title="حذف"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal create unit */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-neutral-900 rounded-2xl p-6 w-full max-w-md shadow-xl border border-neutral-200 dark:border-neutral-800 space-y-4"
            >
              <div className="flex items-center justify-between pb-3 border-b border-neutral-100 dark:border-neutral-800">
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                  <Ruler className="w-5 h-5 text-primary-500" />
                  افزودن واحد سنجش جدید
                </h3>
                <button onClick={() => setIsOpen(false)} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (!form.name) {
                    toast.error('عنوان واحد الزامی است')
                    return
                  }
                  createMutation.mutate(form)
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">عنوان واحد</label>
                  <Input
                    placeholder="مثلا: کیلوگرم"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">نماد / علامت اختصاری</label>
                  <Input
                    placeholder="kg"
                    value={form.symbol}
                    onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                    انصراف
                  </Button>
                  <Button type="submit" isLoading={createMutation.isPending}>
                    ذخیره واحد
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
